import { pullKit, pullPlans, pushKit, pushPlan, type SavedPlan } from "../../../shared/sync";
import type { UserKit } from "../../../shared/types";

/**
 * En la web no hay almacén seguro real (ni Keystore ni cookies HttpOnly compatibles
 * con la app nativa). El token de sesión se queda en localStorage. El riesgo se
 * limita con la caducidad deslizante de 90 días y sin exponer el token en el HTML.
 */

export const localKv = {
  async get(key: string): Promise<string | null> {
    return localStorage.getItem(key);
  },
  async set(key: string, value: string): Promise<void> {
    localStorage.setItem(key, value);
  },
  async del(key: string): Promise<void> {
    localStorage.removeItem(key);
  },
};

export async function readSession(): Promise<{
  id: string;
  email: string;
  name: string;
  token: string;
} | null> {
  const raw = await localKv.get("hermes.session");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as { id: string; email: string; name: string; token: string };
  } catch {
    return null;
  }
}

export async function clearSession(): Promise<void> {
  await localKv.del("hermes.session");
}

function kitKey(userId?: string | null): string {
  return userId ? `hermes.kit.${userId}` : "hermes.kit";
}

function plansKey(userId?: string | null): string {
  return userId ? `hermes.plans.${userId}` : "hermes.plans";
}

export async function readKit(): Promise<UserKit> {
  const session = await readSession();
  if (session?.token) {
    const remote = await pullKit(session.token);
    if (remote) {
      const payload = JSON.stringify(remote);
      await localKv.set(kitKey(session.id), payload);
      await localKv.set("hermes.kit", payload);
      return remote;
    }
  }
  for (const key of [kitKey(session?.id), "hermes.kit"]) {
    const raw = await localKv.get(key);
    if (!raw) continue;
    try {
      const local = JSON.parse(raw) as UserKit;
      // Sin fila en BBDD: subir lo local una vez para no perder el perfil.
      if (session?.token && (local.hobbies?.length || local.gear?.length)) {
        const ok = await pushKit(session.token, local);
        if (!ok) await pushKit(session.token, local);
      }
      return local;
    } catch {
      /* next */
    }
  }
  return { hobbies: [], gear: [] };
}

export async function writeKit(kit: UserKit): Promise<void> {
  const session = await readSession();
  const payload = JSON.stringify(kit);
  await localKv.set(kitKey(session?.id), payload);
  await localKv.set("hermes.kit", payload);
  if (session?.token) {
    const ok = await pushKit(session.token, kit);
    if (!ok) await pushKit(session.token, kit);
  }
}

export async function readSavedPlans(): Promise<SavedPlan[]> {
  const session = await readSession();
  if (session?.token) {
    const remote = await pullPlans(session.token);
    if (remote) {
      const payload = JSON.stringify(remote);
      await localKv.set(plansKey(session.id), payload);
      await localKv.set("hermes.plans", payload);
      return remote;
    }
  }
  for (const key of [plansKey(session?.id), "hermes.plans"]) {
    const raw = await localKv.get(key);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw) as SavedPlan[];
      if (Array.isArray(parsed)) return parsed;
    } catch {
      /* next */
    }
  }
  return [];
}

export async function savePlans(plans: SavedPlan[]): Promise<void> {
  const session = await readSession();
  if (!session?.token) throw new Error("Inicia sesión para guardar planes");
  for (const plan of plans) {
    const ok = await pushPlan(session.token, plan);
    if (!ok) await pushPlan(session.token, plan);
  }
  const all = await pullPlans(session.token);
  if (all) {
    const payload = JSON.stringify(all);
    await localKv.set(plansKey(session.id), payload);
    await localKv.set("hermes.plans", payload);
  }
}

export type { UserKit, SavedPlan };
